import { FC, useEffect, useMemo, useState } from "react";
import { BULLETIN_SUMMARY, PAGE_SIZE_OPTIONS } from "../../utils/constants";
import { getBulletins } from "../../services/Bulletins/getBulletins";
import SearchTable from "../../components/template/Search/SearchTable";
import { TableConfig } from "../../types/Components/Table/TableConfig";
import TableBulletins from "./TableBulletins";
import { useFilter } from "../../hooks/filter/useFilter";
import { useForm } from "react-hook-form";
import { FilterConfig } from "../../types/Components/Table/FilterConfig";
import { Option } from "../../types/General/Option";
import useFetchData from "../../hooks/data/useFetchData";
import { getProvince } from "../../services/TabelasAuxiliares/getProvince";
import { getCountyById } from "../../services/TabelasAuxiliares/getCountyById";
import { bulletinsTableSelectFilterData } from "./data/bulletinsTableSelectFilterData";
import { updateSelectedFilterItemsName } from "../../helpers/updateSelectedFilterItemsName";
import { getFiltersArray } from "../../helpers/getFiltersArray";
import { Dayjs } from "dayjs";
import useTableRendering from "../../hooks/table/useTableRendering";
import { PaginationConfig } from "../../types/Components/Table/PaginationConfig";
import { getBulletinTypes } from "../../services/Bulletins/getBulletinTypes";
import { PaginationValues } from "../../types/General/PaginationValues";
import AlertComponent from "../../components/atom/Alert/AlertComponent";
import { Box, Button, Flex } from "@chakra-ui/react";
import { ModalConfig } from "../../types/Components/Modal/ModalConfig";
import { useCustomModal } from "../../hooks/modal/useCustomModal";
import ModalBulletinGraph from "./component/ModalBulletinGraph";
import { GraphInfo } from "../../types/Graph/GraphInfo";
import { getBulletinIndicator } from "../../services/Bulletins/getBulletinIndicator";
import { useAuth } from "../../provider/AuthProvider";
import { applyRoleBasedFilters } from "../../helpers/Filter/authUserFilter";

interface FilterState {
  TypeId: Option | null;
  Month: Dayjs | null;
  Year: Dayjs | null;
  ProvinceId: Option | null;
  CountyId: Option | null;
}

interface OptionsState {
  typeOptions: Option[];
  provinceOptions: Option[];
  countyOptions: Option[];
}

const Bulletins: FC = () => {
  const { auth } = useAuth();
  const user = auth.user;
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorFindingData, setErrorFindingData] = useState(false);
  const [dataTable, setDataTable] = useState<any[]>([]);
  const [selectedFilterItems, setSelectedFilterItems] = useState<string>("");
  const [totalItems, setTotalItems] = useState<number>(0);
  const [isSearching, setIsSearching] = useState<boolean>(true);
  const [filterState, setFilterState] = useState<FilterState>({
    TypeId: null,
    Month: null,
    Year: null,
    ProvinceId:
      user?.role?.level?.id >= 4 && user?.province
        ? { id: user.province.id, name: user.province.name }
        : null,
    CountyId:
      user?.role?.level?.id === 5 && user?.county
        ? { id: user.county.id, name: user.county.name }
        : null,
  });
  const [optionsState, setOptionsState] = useState<OptionsState>({
    typeOptions: [] as Option[],
    provinceOptions: [] as Option[],
    countyOptions: [] as Option[],
  });
  const {
    control,
    setValue,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();
  const filterNames = useMemo(
    () => ["TypeId", "Month", "Year", "ProvinceId", "CountyId", "NameOrId"],
    [],
  );
  const { filters, setFilter } = useFilter({ setValue, filterNames });
  const [defaultGraphValue, setDefaultGraphValue] = useState<GraphInfo[]>([]);
  const [countyLoaded, setCountyLoaded] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = (
      filterValues?: any,
      paginationValues?: PaginationValues,
    ) => {
      setErrorFindingData(false);
      setIsLoaded(false);
      const updatedFilters = applyRoleBasedFilters(
        user,
        filterValues,
        setFilter,
      );
      setTimeout(() => {
        getBulletins(undefined, updatedFilters, paginationValues)
          .then((result) => {
            setDataTable(result.content);
            setTotalItems(result.totalItems);
            setIsLoaded(true);
            setIsSearching(false);
          })
          .catch((error) => {
            console.log("API Error:", error);
            setErrorFindingData(true);
            setIsSearching(false);
          });
      }, 1000);
    };
    isSearching &&
      fetchData(filters, {
        CurrentPage: currentPage,
        PageSize: pageSize,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearching]);

  const { isLoaded: provinceLoaded } = useFetchData(getProvince, (data) =>
    setOptionsState((prev) => ({ ...prev, provinceOptions: data })),
  );

  const { isLoaded: typeLoaded } = useFetchData(getBulletinTypes, (data) =>
    setOptionsState((prev) => ({ ...prev, typeOptions: data })),
  );

  useEffect(() => {
    const updateCountyOptions = () => {
      setCountyLoaded(false);
      if (filterState.ProvinceId) {
        getCountyById({ ParentId: filterState.ProvinceId.id })
          .then((result) => {
            setOptionsState((prev) => ({
              ...prev,
              countyOptions: result,
            }));
          })
          .finally(() => {
            setCountyLoaded(true);
          });
      } else {
        setCountyLoaded(true);
        setOptionsState((prev) => ({ ...prev, countyOptions: [] }));
      }
    };
    updateCountyOptions();
  }, [filterState.ProvinceId]);

  useEffect(() => {
    const displayFormats = {
      Month: "MMMM",
      Year: "YYYY",
    };
    updateSelectedFilterItemsName(
      filterState,
      filters,
      setSelectedFilterItems,
      filterNames,
      displayFormats,
    );
  }, [filters, filterState, filterNames]);

  const filtersArray = getFiltersArray(filterNames, filters, setFilter);

  const filterChildren = bulletinsTableSelectFilterData(
    optionsState.typeOptions,
    optionsState.provinceOptions,
    optionsState.countyOptions,
    control,
    setFilter,
    (TypeId: any) => setFilterState((prev) => ({ ...prev, type: TypeId })),
    (Month: any) => setFilterState((prev) => ({ ...prev, Month: Month })),
    (Year: any) => setFilterState((prev) => ({ ...prev, Year: Year })),
    (ProvinceId: any) =>
      setFilterState((prev) => ({ ...prev, ProvinceId: ProvinceId })),
    (CountyId: any) =>
      setFilterState((prev) => ({ ...prev, CountyId: CountyId })),
    filterState.ProvinceId,
    filterState.Month,
    filterState.Year,
    provinceLoaded,
    countyLoaded,
    typeLoaded,
    user,
  );

  const tableConfig: TableConfig = {
    tableName: "Lista de Boletins",
    dataTable,
    setDataTable,
    renderTable: (dataTable) => (
      <TableBulletins dataTable={dataTable} isLoaded={isLoaded} />
    ),
    isLoaded,
    errorFindingData,
    searchTitle: "Pesquisar pelo nome ou ID",
  };

  const filterConfig: FilterConfig = {
    filterChildren,
    filters: filtersArray,
    selectedFilterItem: selectedFilterItems,
  };

  const { currentPage, totalPages, handlePageChange, renderContent } =
    useTableRendering(tableConfig, pageSize, totalItems, setIsSearching);

  const paginationConfig: PaginationConfig = {
    pageSize,
    currentPage,
    totalPages,
    handlePageChange,
    renderContent,
    setPageSize,
    totalItems,
  };

  const handleSelectBulletinGraph = () => {
    openModal("Gerir gráfico(s)", "Guardar");

    const initialGraphs = defaultGraphValue.map((graph) => ({
      indicatorOptions: {
        id: graph.indicator.id,
        name: graph.indicator.name,
      },
      typeOption: {
        id: graph.graphicType.id,
        name: graph.graphicType.name,
      },
    }));

    setValue("graphs", initialGraphs);
  };

  const { isOpen, title, buttonTitle, openModal, closeModal } = useCustomModal(
    "",
    "",
  );

  const modalConfig: ModalConfig = {
    isOpen,
    title,
    buttonTitle,
    openModal,
    closeModal,
  };

  useEffect(() => {
    const fetchData = () => {
      getBulletinIndicator()
        .then((result) => {
          setDefaultGraphValue(result);
        })
        .catch((error) => {
          console.log("API Error:", error);
        });
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Flex direction="column" gap="2.5">
      <SearchTable
        summaryName={BULLETIN_SUMMARY}
        showModal
        searchTableName="Boletins"
        tableConfig={tableConfig}
        filterConfig={filterConfig}
        paginationConfig={paginationConfig}
        setIsSearching={setIsSearching}
        setDates={[
          (Month: Dayjs | null) =>
            setFilterState((prev) => ({
              ...prev,
              Month: Month,
            })),
          (Year: Dayjs | null) =>
            setFilterState((prev) => ({ ...prev, Year: Year })),
        ]}
        alertDateCountdownDescription="Geração automática do próximo boletim"
        alertElement={
          user.permissions.includes("UpdateBulletinIndicators") ? (
            <AlertComponent
              status="info"
              title="Os indicadores que serão representados nos gráficos de todos os boletins"
              description="Caso pretenda alterar os indicadores, clique no botão “Gerir gráfico(s)”"
              borderColor="blue.500"
              buttonElement={
                <Box>
                  <Button variant="primary" onClick={handleSelectBulletinGraph}>
                    Gerir gráfico(s)
                  </Button>
                </Box>
              }
            />
          ) : null
        }
      >
        <ModalBulletinGraph
          control={control}
          errors={errors}
          handleSubmit={handleSubmit}
          modalConfig={modalConfig}
          watch={watch}
        />
      </SearchTable>
    </Flex>
  );
};

export default Bulletins;
